import type { ReactNode } from 'react';
import { useControllableState } from '../../hooks/useControllableState';
import { Avatar } from '../Avatar/Avatar';
import { Badge } from '../Badge/Badge';
import { Divider } from '../Divider/Divider';
import { SparkIcon } from '../SparkIcon';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import { NavigationMainButton } from './NavigationMainButton';
import { NavigationTab, type NavigationTabState } from './NavigationTab';
import './Navigation.css';

export type NavigationTabItem = {
  label: string;
  state?: NavigationTabState;
  href?: string;
  onClick?: () => void;
};

/** Top-level navigation bar with logo, tabs, notification bell, settings, and avatar. */
export type NavigationProps = {
  tabs?: NavigationTabItem[];
  /** Controlled active tab (label). */
  activeTab?: string;
  /** Uncontrolled default active tab (label). */
  defaultActiveTab?: string;
  /** Called when the active tab changes. */
  onTabChange?: (tab: string) => void;
  showBeta?: boolean;
  notificationCount?: string;
  /** Whether to show the notification bell button. Defaults to true. */
  showNotification?: boolean;
  onNotificationClick?: () => void;
  /** Whether to show the settings gear button. Defaults to true. */
  showSettings?: boolean;
  onSettingsClick?: () => void;
  /** Rendered at the start of the right-side actions cluster, e.g. a `NavigationActionLink`. */
  leadingActions?: ReactNode;
  /** Rendered after `leadingActions` and before the notification bell, e.g. an action button. */
  actionsStart?: ReactNode;
  rightExtra?: ReactNode;
  /**
   * Phone / tablet layout: the brand and the actions cluster share the first
   * row (the cluster wraps under the brand when it does not fit) and the tabs
   * move to a full-width, horizontally scrollable second row. The default
   * single-row markup is untouched when `false`.
   */
  stacked?: boolean;
  className?: string;
};

export function Navigation({
  tabs = [],
  activeTab,
  defaultActiveTab = '',
  onTabChange,
  showBeta = true,
  notificationCount,
  showNotification = true,
  onNotificationClick,
  showSettings = true,
  onSettingsClick,
  leadingActions,
  actionsStart,
  rightExtra,
  stacked = false,
  className = '',
}: NavigationProps) {
  const [currentTab, setCurrentTab] = useControllableState(activeTab, defaultActiveTab, onTabChange);

  const rootClasses = ['spark-navigation', stacked ? 'spark-navigation--stacked' : '', className]
    .filter(Boolean)
    .join(' ');

  function handleTabClick(tab: NavigationTabItem) {
    setCurrentTab(tab.label);
    tab.onClick?.();
  }

  function resolveTabState(tab: NavigationTabItem): NavigationTabState {
    if (tab.state === 'disabled') return 'disabled';
    if (currentTab) {
      return tab.label === currentTab ? 'active' : 'inactive';
    }
    return tab.state ?? 'inactive';
  }

  const tabsNav = (
    <nav className="spark-navigation-tabs" aria-label="Main">
      {tabs.map((tab) => (
        <NavigationTab
          key={tab.label}
          label={tab.label}
          state={resolveTabState(tab)}
          href={tab.href}
          onClick={() => handleTabClick(tab)}
        />
      ))}
    </nav>
  );

  return (
    <header className={rootClasses}>
      <div className="spark-navigation-left">
        <div className="spark-navigation-brand">
          <img
            className="spark-navigation-logo"
            src={sparkAssetUrl('logo/brandmark-white-logo-with-text.svg')}
            alt="SpecificAI"
          />
          {showBeta ? <span className="spark-navigation-beta">Beta</span> : null}
        </div>
        {stacked ? null : (
          <>
            <Divider direction="vertical" bg="on-dark" className="spark-navigation-divider spark-navigation-divider-fill" />
            {tabsNav}
          </>
        )}
      </div>
      <div className="spark-navigation-right">
        <div className="spark-navigation-actions">
          {leadingActions ? (
            <div className="spark-navigation-leading-actions">{leadingActions}</div>
          ) : null}
          {actionsStart}
          {showNotification && (
            <NavigationMainButton
              aria-label="Notifications"
              showTooltip={false}
              badge={notificationCount ? <Badge amount={notificationCount} className="spark-navigation-badge" /> : undefined}
              onClick={onNotificationClick}
            >
              <SparkIcon slug="bell" size="medium" />
            </NavigationMainButton>
          )}
          {showSettings && (
            <NavigationMainButton aria-label="Settings" showTooltip={false} onClick={onSettingsClick}>
              <SparkIcon slug="settings" size="medium" />
            </NavigationMainButton>
          )}
          {rightExtra ?? <Avatar size="small" type="image" />}
        </div>
      </div>
      {/* Second row; skipped when there are no tabs so the bar stays one row tall. */}
      {stacked && tabs.length > 0 ? tabsNav : null}
    </header>
  );
}

export { NavigationTab, type NavigationTabState } from './NavigationTab';
